<template>
  <div>
    <button class="buscador-fab" @click="abrir" title="Buscar (Ctrl+K)">
      <ion-icon :icon="searchOutline"></ion-icon>
    </button>

    <div v-if="abierto" class="buscador-overlay" @mousedown.self="cerrar">
      <div class="buscador-caja">
        <div class="buscador-input-wrap">
          <ion-icon :icon="searchOutline" class="buscador-input-icono"></ion-icon>
          <input
            ref="input"
            v-model="query"
            type="text"
            placeholder="Buscar siniestros, clientes, colaboradores..."
            class="buscador-input"
            @keydown.down.prevent="mover(1)"
            @keydown.up.prevent="mover(-1)"
            @keydown.enter.prevent="irASeleccionado"
          />
          <span class="buscador-esc">esc</span>
        </div>

        <div class="buscador-resultados" v-if="query.length >= 2">
          <div v-if="cargando" class="buscador-estado">Buscando...</div>
          <div v-else-if="error" class="buscador-estado buscador-error">{{ error }}</div>
          <div v-else-if="totalResultados === 0" class="buscador-estado">Sin resultados para "{{ query }}"</div>

          <template v-else>
            <div v-if="resultados.siniestros.length" class="buscador-grupo">
              <div class="buscador-grupo-titulo">SINIESTROS</div>
              <div
                v-for="s in resultados.siniestros"
                :key="'s-'+s.id"
                :class="['buscador-item', esSeleccionado('siniestro', s.id) && 'buscador-item-activo']"
                @mouseenter="seleccionarHover('siniestro', s.id)"
                @click="irA(itemDestino('siniestro', s))"
              >
                <div class="buscador-avatar buscador-avatar-siniestro">
                  <ion-icon :icon="documentTextOutline"></ion-icon>
                </div>
                <div class="buscador-item-texto">
                  <div class="buscador-item-titulo">
                    {{ s.expediente || s.matricula || ('Siniestro #' + s.id) }}
                  </div>
                  <div class="buscador-item-subtitulo">
                    {{ nombresClientes(s) }}<span v-if="s.matricula"> · {{ s.matricula }}</span>
                  </div>
                  <div v-for="(c, ci) in (s.coincidencias || []).slice(0, 2)" :key="ci" class="buscador-coincidencia">
                    <span class="buscador-coincidencia-campo">{{ c.campo }}:</span>
                    <span v-html="fragmentoResaltado(c.valor)"></span>
                  </div>
                </div>
                <span class="buscador-badge buscador-badge-siniestro">SINIESTRO</span>
              </div>
            </div>

            <div v-if="resultados.clientes.length" class="buscador-grupo">
              <div class="buscador-grupo-titulo">CLIENTES</div>
              <div
                v-for="c in resultados.clientes"
                :key="'c-'+c.id"
                :class="['buscador-item', esSeleccionado('cliente', c.id) && 'buscador-item-activo']"
                @mouseenter="seleccionarHover('cliente', c.id)"
                @click="irA(itemDestino('cliente', c))"
              >
                <div class="buscador-avatar" :style="{ background: colorParaTexto(c.name) }">
                  {{ iniciales(c.name) }}
                </div>
                <div class="buscador-item-texto">
                  <div class="buscador-item-titulo">{{ c.name }}</div>
                  <div class="buscador-item-subtitulo">
                    {{ c.dni }}<span v-if="c.movil"> · {{ c.movil }}</span>
                  </div>
                  <div v-for="(m, mi) in coincidenciasRelevantes(c, ['Nombre', 'DNI'])" :key="mi" class="buscador-coincidencia">
                    <span class="buscador-coincidencia-campo">{{ m.campo }}:</span>
                    <span v-html="fragmentoResaltado(m.valor)"></span>
                  </div>
                </div>
                <span class="buscador-badge buscador-badge-cliente">CLIENTE</span>
              </div>
            </div>

            <div v-if="resultados.usuarios.length" class="buscador-grupo">
              <div class="buscador-grupo-titulo">USUARIOS</div>
              <div
                v-for="u in resultados.usuarios"
                :key="'u-'+u.id"
                :class="['buscador-item', esSeleccionado('usuario', u.id) && 'buscador-item-activo']"
                @mouseenter="seleccionarHover('usuario', u.id)"
                @click="irA(itemDestino('usuario', u))"
              >
                <div class="buscador-avatar" :style="{ background: colorParaTexto(u.name) }">
                  {{ iniciales(u.name) }}
                </div>
                <div class="buscador-item-texto">
                  <div class="buscador-item-titulo">{{ u.name }}</div>
                  <div class="buscador-item-subtitulo">{{ u.email }}</div>
                  <div v-for="(m, mi) in coincidenciasRelevantes(u, ['Nombre', 'Email'])" :key="mi" class="buscador-coincidencia">
                    <span class="buscador-coincidencia-campo">{{ m.campo }}:</span>
                    <span v-html="fragmentoResaltado(m.valor)"></span>
                  </div>
                </div>
                <span class="buscador-badge buscador-badge-usuario">USUARIO</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { IonIcon } from '@ionic/vue';
import { searchOutline, documentTextOutline } from 'ionicons/icons';
import axios from '@/plugins/axios';
import { mapGetters } from 'vuex';

const PALETA_AVATAR = ['#6366F1', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];

export default {
  name: 'BuscadorGlobal',
  components: { IonIcon },
  data() {
    return {
      abierto: false,
      query: '',
      cargando: false,
      error: null,
      resultados: { siniestros: [], clientes: [], usuarios: [] },
      seleccion: null,
      debounceId: null,
      searchOutline,
      documentTextOutline,
    };
  },
  computed: {
    ...mapGetters(['getUser']),
    listaPlano() {
      return [
        ...this.resultados.siniestros.map((s) => ({ tipo: 'siniestro', id: s.id, item: s })),
        ...this.resultados.clientes.map((c) => ({ tipo: 'cliente', id: c.id, item: c })),
        ...this.resultados.usuarios.map((u) => ({ tipo: 'usuario', id: u.id, item: u })),
      ];
    },
    totalResultados() {
      return this.listaPlano.length;
    },
  },
  watch: {
    query() {
      clearTimeout(this.debounceId);

      if (this.query.trim().length < 2) {
        this.resultados = { siniestros: [], clientes: [], usuarios: [] };
        this.error = null;
        return;
      }

      this.debounceId = setTimeout(() => this.buscar(), 300);
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onAtajoGlobal);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onAtajoGlobal);
  },
  methods: {
    onAtajoGlobal(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        this.abrir();
      } else if (e.key === 'Escape' && this.abierto) {
        this.cerrar();
      }
    },
    abrir() {
      if (this.getUser && this.getUser.role && this.getUser.role.name === 'Cliente') {
        return;
      }
      this.abierto = true;
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    cerrar() {
      this.abierto = false;
      this.query = '';
      this.resultados = { siniestros: [], clientes: [], usuarios: [] };
      this.seleccion = null;
    },
    async buscar() {
      this.cargando = true;
      this.error = null;

      try {
        const { data } = await axios.get('/api/buscador', { params: { q: this.query.trim() } });
        this.resultados = data.data;
        this.seleccion = this.listaPlano.length ? { tipo: this.listaPlano[0].tipo, id: this.listaPlano[0].id } : null;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.error && e.response.data.error.message) || 'No se pudo completar la búsqueda';
      } finally {
        this.cargando = false;
      }
    },
    esSeleccionado(tipo, id) {
      return this.seleccion && this.seleccion.tipo === tipo && this.seleccion.id === id;
    },
    seleccionarHover(tipo, id) {
      this.seleccion = { tipo, id };
    },
    mover(delta) {
      const lista = this.listaPlano;
      if (!lista.length) return;

      const actual = lista.findIndex((r) => this.seleccion && r.tipo === this.seleccion.tipo && r.id === this.seleccion.id);
      const siguiente = ((actual === -1 ? 0 : actual) + delta + lista.length) % lista.length;
      this.seleccion = { tipo: lista[siguiente].tipo, id: lista[siguiente].id };
    },
    irASeleccionado() {
      if (!this.seleccion) return;
      const encontrado = this.listaPlano.find((r) => r.tipo === this.seleccion.tipo && r.id === this.seleccion.id);
      if (encontrado) this.irA(this.itemDestino(encontrado.tipo, encontrado.item));
    },
    itemDestino(tipo, item) {
      if (tipo === 'siniestro') return { path: `/documentos/${item.id}` };
      if (tipo === 'cliente') return { name: 'cliente_coloborador', params: { id: item.id } };

      // usuario: sin vista de detalle propia, se navega al listado de su rol
      const listasPorRol = { Colaborador: '/colaboradores', Despacho: '/sub-administradores', Administrador: '/administradores' };
      return { path: listasPorRol[item.role ? item.role.name : ''] || '/escritorio' };
    },
    irA(destino) {
      this.cerrar();
      this.$router.push(destino);
    },
    nombresClientes(siniestro) {
      return (siniestro.clientes || []).map((c) => c.name).join(', ') || 'Sin cliente';
    },
    iniciales(nombre) {
      if (!nombre) return '?';
      return nombre.trim().split(/\s+/).slice(0, 2).map((p) => p[0].toUpperCase()).join('');
    },
    colorParaTexto(texto) {
      const t = texto || '';
      let hash = 0;
      for (let i = 0; i < t.length; i++) hash = t.charCodeAt(i) + ((hash << 5) - hash);
      return PALETA_AVATAR[Math.abs(hash) % PALETA_AVATAR.length];
    },
    // Coincidencias que no sean ya obvias por el título/subtítulo del resultado.
    coincidenciasRelevantes(item, etiquetasYaVisibles) {
      return (item.coincidencias || [])
        .filter((c) => !etiquetasYaVisibles.includes(c.campo))
        .slice(0, 2);
    },
    // Recorta el valor a un fragmento alrededor de la búsqueda y resalta la
    // parte que coincide. Escapa el resto para no permitir HTML/XSS desde
    // datos que en realidad son texto libre (p.ej. observaciones de un
    // siniestro).
    fragmentoResaltado(valor) {
      const escapar = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const texto = String(valor);
      const query = this.query.trim();
      const idx = texto.toLowerCase().indexOf(query.toLowerCase());

      if (idx === -1) return escapar(texto);

      const contexto = 30;
      const inicio = Math.max(0, idx - contexto);
      const fin = Math.min(texto.length, idx + query.length + contexto);

      const prefijo = inicio > 0 ? '…' : '';
      const sufijo = fin < texto.length ? '…' : '';

      const antes = escapar(texto.slice(inicio, idx));
      const medio = escapar(texto.slice(idx, idx + query.length));
      const despues = escapar(texto.slice(idx + query.length, fin));

      return `${prefijo}${antes}<mark class="buscador-resaltado">${medio}</mark>${despues}${sufijo}`;
    },
  },
};
</script>

<style scoped>
.buscador-fab {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: #1f2937;
  color: #fff;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  z-index: 1000;
  transition: transform 0.15s ease, background 0.15s ease;
}

.buscador-fab:hover {
  background: #111827;
  transform: scale(1.05);
}

.buscador-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 12vh;
  z-index: 2000;
}

.buscador-caja {
  width: min(640px, 92vw);
  max-height: 70vh;
  background: #111827;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #1f2937;
}

.buscador-input-wrap {
  display: flex;
  align-items: center;
  padding: 16px 18px;
  border-bottom: 1px solid #1f2937;
  flex-shrink: 0;
}

.buscador-input-icono {
  color: #9ca3af;
  font-size: 20px;
  margin-right: 10px;
}

.buscador-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #f9fafb;
  font-size: 17px;
}

.buscador-input::placeholder {
  color: #6b7280;
}

.buscador-esc {
  color: #6b7280;
  font-size: 12px;
  border: 1px solid #374151;
  border-radius: 4px;
  padding: 2px 6px;
}

.buscador-resultados {
  overflow-y: auto;
  padding: 8px;
}

.buscador-estado {
  color: #9ca3af;
  text-align: center;
  padding: 24px 12px;
  font-size: 14px;
}

.buscador-error {
  color: #f87171;
}

.buscador-grupo {
  margin-bottom: 6px;
}

.buscador-grupo-titulo {
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  padding: 10px 10px 4px;
}

.buscador-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
}

.buscador-item-activo {
  background: rgba(99, 102, 241, 0.18);
}

.buscador-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.buscador-avatar-siniestro {
  background: #374151;
  font-size: 16px;
}

.buscador-item-texto {
  flex: 1;
  min-width: 0;
}

.buscador-item-titulo {
  color: #f3f4f6;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.buscador-item-subtitulo {
  color: #9ca3af;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.buscador-coincidencia {
  color: #6b7280;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.buscador-coincidencia-campo {
  color: #9ca3af;
  font-weight: 600;
  margin-right: 4px;
}

.buscador-resaltado {
  background: rgba(250, 204, 21, 0.25);
  color: #facc15;
  border-radius: 3px;
  padding: 0 2px;
}

.buscador-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 5px;
  flex-shrink: 0;
}

.buscador-badge-siniestro {
  background: rgba(245, 158, 11, 0.18);
  color: #fbbf24;
}

.buscador-badge-cliente {
  background: rgba(16, 185, 129, 0.18);
  color: #34d399;
}

.buscador-badge-usuario {
  background: rgba(14, 165, 233, 0.18);
  color: #38bdf8;
}
</style>
